import { API_URL } from "@/lib";
import { motion, AnimatePresence } from "framer-motion";
import { 
    Loader2, Sparkles, Zap, Users, Code, Server, Database, 
    TrendingUp, Cloud, Shield, CheckCircle2, ArrowRight, Copy, Terminal, 
    Lightbulb // Added Lightbulb icon for the prompts
} from "lucide-react";
import { useState, useCallback, useEffect } from "react";

// --- Configuration & Static Data ---

const SUGGESTED_PROMPTS = [
    "Migrate legacy monolithic .NET app to AWS serverless microservices.",
    "Real-time fraud detection pipeline for high-volume fintech transactions.",
    "Secure, HIPAA-compliant patient data storage with analytics dashboard.",
    "High-availability e-commerce platform handling Black Friday traffic."
];

const TECH_CATEGORIES = {
    "Cloud & Infrastructure": { icon: Cloud, techs: ['AWS', 'Azure', 'GCP', 'Terraform', 'Kubernetes', 'Docker', 'OpenShift'] },
    "Backend": { icon: Code, techs: ['C# .NET', 'Node.js', 'NestJS', 'Go', 'Python', 'Java', 'Serverless'] },
    "Frontend": { icon: Zap, techs: ['React', 'Angular', 'Vue.js', 'TypeScript', 'Tailwind CSS', 'Next.js'] },
    "Data": { icon: Database, techs: ['PostgreSQL', 'MongoDB', 'Redis', 'Kafka', 'RabbitMQ', 'Elasticsearch', 'Snowflake'] },
    "DevOps": { icon: Server, techs: ['GitLab CI', 'GitHub Actions', 'ArgoCD', 'Prometheus', 'Grafana', 'Ansible'] },
    "Security": { icon: Shield, techs: ['Selenium', 'Snyk', 'OWASP', 'Vault', 'IdentityServer'] }
};

const SERVICE_ICONS: Record<string, any> = {
    'Leadership': Users,
    'Architecture': TrendingUp,
    'DevOps/SecOps': Server,
    'Data Engineering': Database
};

const ALL_TECHS = Object.values(TECH_CATEGORIES).flatMap(cat => cat.techs).join(', ');
const HIGH_LEVEL_SERVICES = ['Leadership', 'Architecture', 'DevOps/SecOps', 'Data Engineering'];

export default function SolutionArchitectureAnalyzer() {
    const [challenge, setChallenge] = useState('');
    const [analysis, setAnalysis] = useState<any>(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [processingStep, setProcessingStep] = useState(0);
    const [copied, setCopied] = useState(false);

    // --- Logic ---

    // Simulated processing steps for better UX
    useEffect(() => {
        if (loading) {
            const interval = setInterval(() => {
                setProcessingStep((prev) => (prev < 3 ? prev + 1 : prev));
            }, 1200);
            return () => clearInterval(interval);
        } else {
            setProcessingStep(0);
        }
    }, [loading]);

    const handleSubmit = useCallback(async (e: React.FormEvent) => {
        e.preventDefault();
        if (challenge.length < 10) {
            setError("Please provide a bit more context about your challenge.");
            return;
        }

        setLoading(true);
        setAnalysis(null);
        setError(null);

        // Prompt construction (kept strict for reliability)
        const userQuery = `
            As an Expert Solution Architect, analyze this challenge: "${challenge}".
            
            Context:
            - Services available: ${HIGH_LEVEL_SERVICES.join(', ')}.
            - Tech Stack available: ${ALL_TECHS}.

            Return strict JSON:
            {
              "analysis": "High-level strategic assessment (max 25 words).",
              "services": ["Array of 2-3 relevant Services from list"],
              "keyTechnologies": ["Array of 4-6 critical Technologies from list"],
              "nextSteps": "One concise, actionable next step (max 15 words)."
            }
        `;

        try {
            const payload = {
                contents: [{ parts: [{ text: userQuery }] }],
                generationConfig: { responseMimeType: "application/json" }
            };

            const response = await fetch(API_URL, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(payload)
            });

            if (!response.ok) throw new Error("API Service Unavailable");

            const result = await response.json();
            const textData = result.candidates?.[0]?.content?.parts?.[0]?.text;
            
            if (textData) {
                setAnalysis(JSON.parse(textData));
            } else {
                throw new Error("Invalid response format");
            }
        } catch (e: any) {
            setError(e.message || "Analysis failed. Please try again.");
        } finally {
            setLoading(false);
        }
    }, [challenge]);

    const handleCopy = () => {
        if (!analysis) return;
        const text = `Analysis: ${analysis.analysis}\nStack: ${analysis.keyTechnologies.join(', ')}`;
        navigator.clipboard.writeText(text);
        setCopied(true);
        setTimeout(() => setCopied(false), 2000);
    };

    return (
        <section id="analyzer" className="min-h-screen py-20 bg-[#0B0C15] text-slate-200 selection:bg-indigo-500/30">
            
            {/* Background Ambient Glows */}
            <div className="fixed top-0 left-0 w-full h-full overflow-hidden pointer-events-none z-0">
                <div className="absolute top-[-10%] right-[-5%] w-[500px] h-[500px] bg-indigo-900/20 rounded-full blur-[120px]" />
                <div className="absolute bottom-[10%] left-[-10%] w-[600px] h-[600px] bg-blue-900/10 rounded-full blur-[100px]" />
            </div>

            <div className="relative z-10 max-w-6xl mx-auto px-6">
                
                {/* Header Section */}
                <div className="text-center mb-16 space-y-6">
                    <motion.div 
                        initial={{ opacity: 0, y: -20 }} 
                        animate={{ opacity: 1, y: 0 }}
                        className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-indigo-500/10 border border-indigo-500/20 text-indigo-300 text-xs font-semibold tracking-wider uppercase"
                    >
                        <Sparkles className="w-3 h-3" />
                        AI-Powered Architecture
                    </motion.div>
                    
                    <motion.h2 
                        initial={{ opacity: 0 }} 
                        animate={{ opacity: 1 }}
                        transition={{ delay: 0.1 }}
                        className="text-4xl md:text-6xl font-bold text-white tracking-tight"
                    >
                        Build the <span className="text-transparent bg-clip-text bg-gradient-to-r from-indigo-400 to-cyan-300">Right Solution</span>
                    </motion.h2>
                    
                    <motion.p 
                        initial={{ opacity: 0 }} 
                        animate={{ opacity: 1 }}
                        transition={{ delay: 0.2 }}
                        className="text-lg text-slate-400 max-w-2xl mx-auto"
                    >
                        Describe your technical challenge. Our AI simulates a CTO & Architect to recommend the precise stack and strategy.
                    </motion.p>
                </div>

                {/* Input Area (The Console) */}
                <div className="grid lg:grid-cols-12 gap-8 mb-20">
                    <div className="lg:col-span-8 lg:col-start-3">
                        <motion.div 
                            className="bg-slate-900/60 backdrop-blur-xl border border-slate-800 rounded-2xl p-1 shadow-2xl relative overflow-hidden group"
                            initial={{ y: 20, opacity: 0 }}
                            animate={{ y: 0, opacity: 1 }}
                        >
                            {/* Glowing border effect */}
                            <div className="absolute inset-0 bg-gradient-to-r from-indigo-500/20 to-cyan-500/20 opacity-0 group-focus-within:opacity-100 transition-opacity duration-500 pointer-events-none" />
                            
                            <form onSubmit={handleSubmit} className="relative bg-[#0F111A] rounded-xl p-6 z-10">
                                <div className="flex items-center justify-between mb-4">
                                    <label htmlFor="challenge" className="text-sm font-medium text-slate-400 flex items-center gap-2">
                                        <Terminal className="w-4 h-4" />
                                        Input Parameter: Challenge Context
                                    </label>
                                    {challenge && (
                                        <button 
                                            type="button" 
                                            onClick={() => setChallenge('')}
                                            className="text-xs text-slate-500 hover:text-white transition-colors"
                                        >
                                            Clear
                                        </button>
                                    )}
                                </div>
                                
                                <textarea
                                    id="challenge"
                                    value={challenge}
                                    onChange={(e) => setChallenge(e.target.value)}
                                    disabled={loading}
                                    rows={4}
                                    className="w-full bg-transparent border-none text-lg text-white placeholder-slate-600 focus:ring-0 resize-none p-0"
                                    placeholder="e.g. We need to handle 50TB of sensor data daily and visualize it in real-time..."
                                />

                                <div className="mt-6 flex items-center justify-between border-t border-slate-800 pt-4">
                                    <div className="hidden md:flex gap-2">
                                        {/* Decorative status dots */}
                                        <div className="w-2 h-2 rounded-full bg-emerald-500/50 animate-pulse" />
                                        <span className="text-xs text-slate-500">System Ready</span>
                                    </div>
                                    <button
                                        type="submit"
                                        disabled={loading || !challenge}
                                        className="flex items-center gap-2 px-6 py-2.5 bg-indigo-600 hover:bg-indigo-500 disabled:bg-slate-800 disabled:text-slate-600 text-white font-medium rounded-lg transition-all duration-200 shadow-lg shadow-indigo-900/20"
                                    >
                                        {loading ? (
                                            <Loader2 className="w-4 h-4 animate-spin" />
                                        ) : (
                                            <>
                                                Analyze Architecture <ArrowRight className="w-4 h-4" />
                                            </>
                                        )}
                                    </button>
                                </div>
                            </form>
                        </motion.div>

                        {/* UPDATED: Grid Layout for Suggested Prompts */}
                        {!loading && !analysis && (
                            <motion.div 
                                className="mt-8"
                                initial={{ opacity: 0 }} animate={{ opacity: 1 }}
                            >
                                <p className="text-center text-slate-500 text-sm mb-4">Or select a sample scenario:</p>
                                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                    {SUGGESTED_PROMPTS.map((prompt, i) => (
                                        <button
                                            key={i}
                                            onClick={() => setChallenge(prompt)}
                                            className="group relative text-left p-4 rounded-xl bg-slate-800/30 hover:bg-slate-800 border border-slate-700/50 hover:border-indigo-500/50 transition-all duration-300 flex items-start gap-3"
                                        >
                                            <div className="mt-0.5 p-1.5 rounded-md bg-indigo-500/10 text-indigo-400 group-hover:text-white group-hover:bg-indigo-500/50 transition-colors shrink-0">
                                                <Lightbulb className="w-4 h-4" />
                                            </div>
                                            <span className="text-sm text-slate-400 group-hover:text-slate-200 leading-relaxed">
                                                {prompt}
                                            </span>
                                        </button>
                                    ))}
                                </div>
                            </motion.div>
                        )}

                        {error && (
                            <motion.div 
                                initial={{ opacity: 0, y: 10 }} 
                                animate={{ opacity: 1, y: 0 }}
                                className="mt-6 p-4 bg-red-500/10 border border-red-500/20 rounded-xl text-red-400 text-sm text-center"
                            >
                                {error}
                            </motion.div>
                        )}
                    </div>
                </div>

                {/* Loading State Overlay */}
                <AnimatePresence mode="wait">
                    {loading && (
                        <motion.div 
                            key="loading"
                            initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
                            className="flex flex-col items-center justify-center py-12"
                        >
                            <div className="w-16 h-16 mb-6 relative">
                                <div className="absolute inset-0 border-4 border-indigo-500/30 rounded-full"></div>
                                <div className="absolute inset-0 border-t-4 border-indigo-400 rounded-full animate-spin"></div>
                            </div>
                            <div className="h-6 overflow-hidden relative w-full max-w-xs text-center">
                                <AnimatePresence mode="wait">
                                    <motion.span
                                        key={processingStep}
                                        initial={{ y: 20, opacity: 0 }}
                                        animate={{ y: 0, opacity: 1 }}
                                        exit={{ y: -20, opacity: 0 }}
                                        className="text-indigo-300 font-medium block absolute w-full"
                                    >
                                        {["Parsing Challenge...", "Scanning Service Models...", "Selecting Tech Stack...", "Finalizing Blueprint..."][processingStep]}
                                    </motion.span>
                                </AnimatePresence>
                            </div>
                        </motion.div>
                    )}

                    {/* Results Dashboard */}
                    {analysis && !loading && (
                        <motion.div 
                            key="results"
                            initial="hidden" animate="visible"
                            variants={{
                                hidden: { opacity: 0 },
                                visible: { opacity: 1, transition: { staggerChildren: 0.1 } }
                            }}
                            className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6"
                        >
                            {/* Card 1: Strategic Assessment (Wide) */}
                            <motion.div 
                                variants={{ hidden: { y: 20, opacity: 0 }, visible: { y: 0, opacity: 1 } }}
                                className="lg:col-span-2 bg-slate-800/40 backdrop-blur-md border border-slate-700/50 rounded-2xl p-8 flex flex-col relative overflow-hidden"
                            >
                                <div className="absolute top-0 right-0 w-24 h-24 bg-indigo-500/10 rounded-bl-full -mr-4 -mt-4" />
                                <div className="flex items-center justify-between mb-4">
                                    <h3 className="text-indigo-400 font-bold text-lg flex items-center gap-2">
                                        <TrendingUp className="w-5 h-5" /> Strategy
                                    </h3>
                                    <button onClick={handleCopy} className="text-slate-500 hover:text-white transition-colors">
                                        {copied ? <CheckCircle2 className="w-5 h-5 text-emerald-500" /> : <Copy className="w-5 h-5" />}
                                    </button>
                                </div>
                                <p className="text-xl text-white leading-relaxed font-light">
                                    {analysis.analysis}
                                </p>
                            </motion.div>

                            {/* Card 2: Required Services */}
                            <motion.div 
                                variants={{ hidden: { y: 20, opacity: 0 }, visible: { y: 0, opacity: 1 } }}
                                className="bg-slate-800/40 backdrop-blur-md border border-slate-700/50 rounded-2xl p-6"
                            >
                                <h3 className="text-cyan-400 font-bold text-sm uppercase tracking-wider mb-4 flex items-center gap-2">
                                    <Users className="w-4 h-4" /> Teams Needed
                                </h3>
                                <div className="space-y-3">
                                    {analysis.services.map((srv: string, i: number) => {
                                        const Icon = SERVICE_ICONS[srv] || Zap;
                                        return (
                                            <div key={i} className="flex items-center gap-3 p-3 rounded-lg bg-slate-900/50 border border-slate-800">
                                                <div className="p-2 rounded-md bg-slate-800 text-cyan-300">
                                                    <Icon className="w-4 h-4" />
                                                </div>
                                                <span className="text-sm font-medium">{srv}</span>
                                            </div>
                                        );
                                    })}
                                </div>
                            </motion.div>

                            {/* Card 3: Action Plan */}
                            <motion.div 
                                variants={{ hidden: { y: 20, opacity: 0 }, visible: { y: 0, opacity: 1 } }}
                                className="bg-gradient-to-br from-indigo-900/40 to-slate-900/40 border border-indigo-500/30 rounded-2xl p-6"
                            >
                                <h3 className="text-indigo-300 font-bold text-sm uppercase tracking-wider mb-4 flex items-center gap-2">
                                    <CheckCircle2 className="w-4 h-4" /> Next Action
                                </h3>
                                <p className="text-white font-medium text-lg">
                                    {analysis.nextSteps}
                                </p>
                            </motion.div>

                            {/* Card 4: Tech Stack (Full Width Row) */}
                            <motion.div 
                                variants={{ hidden: { y: 20, opacity: 0 }, visible: { y: 0, opacity: 1 } }}
                                className="lg:col-span-4 bg-slate-900/30 border border-slate-800 rounded-2xl p-8"
                            >
                                <h3 className="text-slate-400 font-bold text-sm uppercase tracking-wider mb-6 flex items-center gap-2">
                                    <Code className="w-4 h-4" /> Recommended Technology Stack
                                </h3>
                                <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-4">
                                    {analysis.keyTechnologies.map((tech: string, i: number) => (
                                        <div key={i} className="group flex items-center gap-3 p-4 rounded-xl bg-slate-800 hover:bg-slate-700 border border-slate-700 hover:border-indigo-500/50 transition-all duration-300">
                                            <div className="w-2 h-8 rounded-full bg-indigo-500/30 group-hover:bg-indigo-400 transition-colors" />
                                            <span className="font-medium text-slate-200 group-hover:text-white">{tech}</span>
                                        </div>
                                    ))}
                                </div>
                            </motion.div>

                        </motion.div>
                    )}
                </AnimatePresence>
            </div>
        </section>
    );
}